/**
 * The once-per-device card under the log's actions that asks a parent in a
 * browser tab on a phone or tablet to add Babia to the Home Screen. Babia is
 * a website, and on iPhone and iPad bottle alerts work only from there. The
 * steps fit the browser (lib/home-screen.ts); on Android with the browser's
 * install prompt saved, it is one Install button. It never shows in night
 * mode, and it shows until answered: Not now, or an install, retires it. The
 * Home Screen app and desktops never show it.
 */
import { Link } from "@tanstack/react-router";
import { HousePlus, Share, SquarePlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { captureError, displayMode, track } from "../../lib/analytics.ts";
import { type HomeScreenStep, homeScreenStep } from "../../lib/home-screen.ts";
import { META_APP_NAMES } from "../../lib/in-app-browser.ts";
import { promptInstall, useInstallPrompt } from "../../lib/install-prompt.ts";
import { usePushConfig } from "../../lib/queries.ts";
import { Button } from "../ui/button.tsx";

const STORAGE_KEY = "babia.homeScreenStep";

/** "shown" once it has shown on this device, "done" once answered. */
function stored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage disabled: never nag on every visit.
    return "done";
  }
}

function store(value: "shown" | "done"): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

/**
 * The step to show now, or null; `ready` holds it back (night mode, a
 * read-only log). `retire` hides it for good on this device.
 */
export function useHomeScreenStep(ready: boolean): {
  step: HomeScreenStep | null;
  retire: () => void;
} {
  const { canPrompt, installed } = useInstallPrompt();
  const [answered, setAnswered] = useState(() => stored() === "done");
  const step = useMemo(
    () =>
      homeScreenStep({
        userAgent: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        standalone: displayMode() === "standalone",
        installPrompt: canPrompt,
      }),
    [canPrompt],
  );
  const open = ready && !answered && !installed && step !== null;
  // Counted once per device, the first time it shows.
  useEffect(() => {
    if (!open || stored() !== null) return;
    store("shown");
    track("home screen step shown", { step: step.kind });
  }, [open, step]);
  // Installed from the browser's own menu: the tab never asks again.
  useEffect(() => {
    if (installed) store("done");
  }, [installed]);
  return {
    step: open ? step : null,
    retire: () => {
      store("done");
      setAnswered(true);
    },
  };
}

const Steps = ({ children }: { children: React.ReactNode }) => (
  <ol className="mt-2 list-decimal space-y-1 pl-5 text-[15px] leading-snug">{children}</ol>
);

const icon = "inline size-[17px] align-[-3px]";

export function HomeScreenCard({ step, onDone }: { step: HomeScreenStep; onDone: () => void }) {
  // Alerts are named only where they need the Home Screen, and only on a
  // server that sends them.
  const alerts = usePushConfig().data?.enabled === true;
  const ios =
    step.kind === "safari" ||
    step.kind === "ios-browser" ||
    (step.kind === "in-app" && step.os === "ios");
  const screen = ios ? "Home Screen" : "Home screen";
  const [installing, setInstalling] = useState(false);

  function dismiss() {
    track("home screen step dismissed", { step: step.kind });
    onDone();
  }

  async function install() {
    setInstalling(true);
    let outcome: Awaited<ReturnType<typeof promptInstall>> = null;
    try {
      outcome = await promptInstall();
    } catch (err) {
      captureError(err, { handled: true });
    }
    setInstalling(false);
    track("home screen install tapped", { accepted: outcome === "accepted" });
    if (outcome === "accepted") onDone();
  }

  const openIt = (
    <li>
      Open Babia from your {screen}.
      {ios && alerts ? (
        <>
          {" "}
          Then turn on bottle alerts in{" "}
          <Link to="/settings" hash="device" className="underline underline-offset-2">
            Settings
          </Link>
          .
        </>
      ) : null}
    </li>
  );
  const addIt = (
    <li>
      Tap Add to Home Screen <SquarePlus className={icon} aria-hidden="true" />, then Add.
    </li>
  );

  return (
    <section
      aria-labelledby="home-screen-title"
      className="mt-6 border border-divider border-t-[3px] border-t-accent bg-surface px-4 pt-3 pb-2"
    >
      <h2 id="home-screen-title" className="heading flex items-center gap-2 text-[20px]">
        <HousePlus className="size-5 shrink-0 text-accent" aria-hidden="true" />
        Add Babia to your {screen}
      </h2>
      <p className="mt-1.5 text-[15px] leading-snug">
        {step.kind === "in-app"
          ? `Babia is open inside ${step.app ? META_APP_NAMES[step.app] : "another app"}, which can’t add it to your ${screen}. Your browser can.`
          : `It opens like any other app, one tap from your ${screen}${ios && alerts ? ", and bottle alerts work only from there." : "."}`}
      </p>
      {step.kind === "safari" ? (
        <Steps>
          <li>
            Tap Share <Share className={icon} aria-hidden="true" />. No Share button? Tap ⋯ first.
          </li>
          {addIt}
          {openIt}
        </Steps>
      ) : step.kind === "ios-browser" ? (
        <Steps>
          <li>
            Tap Share <Share className={icon} aria-hidden="true" />, next to the address bar.
          </li>
          {addIt}
          {openIt}
        </Steps>
      ) : step.kind === "in-app" ? (
        <Steps>
          <li>
            {step.app
              ? `Tap ${step.os === "ios" ? "⋯" : "⋮"} at the top right, then open this page in your browser.`
              : "Open this page in your browser from this app’s menu."}
          </li>
          <li>Sign in there if Babia asks. Babia then shows you how to add it.</li>
        </Steps>
      ) : step.kind === "android-menu" ? (
        <Steps>
          <li>
            {step.chrome
              ? "Tap ⋮ at the top right, then Install or Add to Home screen."
              : "Open your browser’s menu, then tap Install or Add to Home screen."}
          </li>
          {openIt}
        </Steps>
      ) : null}
      <div className="mt-2 flex items-center gap-1">
        {step.kind === "install" ? (
          <button
            type="button"
            onClick={() => void install()}
            disabled={installing}
            className="primary-btn heading inline-flex min-h-[44px] items-center px-4 text-[15px]"
          >
            Install Babia
          </button>
        ) : null}
        <Button variant="ghost" className="ml-auto text-[14px]" onClick={dismiss}>
          Not now
        </Button>
      </div>
    </section>
  );
}
